import { useMemo, useRef, useSyncExternalStore } from 'react';
import { NotificationController } from './NotificationController';

/**
 * useNotifications — реактивный хук для работы с уведомлениями через useSyncExternalStore.
 *
 * Поведение:
 * - Подписывается на `NotificationController` и возвращает актуальный срез состояния.
 * - Поддерживает селектор: если передан, вернёт выбранный фрагмент; иначе вернёт полный объект
 *   состояния вместе с экшенами (markAsRead, deleteNotification и т.д.).
 * - Внутренне использует стабильный снапшот контроллера (без копирования), что корректно для
 *   concurrent rendering.
 *
 * Примеры:
 *   const n = useNotifications();                    // полный доступ к state + actions
 *   const unread = useNotifications(s => s.unreadCount); // только число непрочитанных
 */
/**
 * Возвращает полный объект состояния с экшенами.
 */
export function useNotifications(): ReturnType<typeof withActions>;
/**
 * Возвращает только выбранный селектором фрагмент.
 * @param selector Функция выбора подмножества из полного объекта (мемоизирована по значению)
 */
export function useNotifications<S>(selector: (full: ReturnType<typeof withActions>) => S): S;
export function useNotifications<S = ReturnType<typeof withActions>>(
  selector?: (full: ReturnType<typeof withActions>) => S
): S | ReturnType<typeof withActions> {
  const controller = NotificationController.getInstance();

  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );

  const full = useMemo(() => withActions(controller, state), [controller, state]);

  if (!selector) return full;

  const selectedRef = useRef<S | undefined>(undefined);
  return useMemo(() => {
    const selected = selector(full);
    if (selectedRef.current !== undefined) {
      try {
        if (JSON.stringify(selectedRef.current) === JSON.stringify(selected)) {
          return selectedRef.current;
        }
      } catch {
        /* noop */
      }
    }
    selectedRef.current = selected;
    return selected;
  }, [full, selector]);
}

/**
 * Добавляет экшены контроллера к снапшоту состояния для удобного потребления в компонентах.
 * @param controller Инстанс `NotificationController`
 * @param snapshot Текущий снапшот состояния из контроллера
 * @returns Полный объект: состояние + функции-экшены
 */
function withActions(
  controller: NotificationController,
  snapshot: ReturnType<NotificationController['getSnapshot']>
) {
  return {
    // State
    notifications: snapshot.notifications,
    markers: snapshot.markers,
    unreadCount: snapshot.unreadCount,
    isLoading: snapshot.isLoading,
    error: snapshot.error,

    // Push state
    pushEnabled: snapshot.pushEnabled,
    pushPermission: snapshot.pushPermission,
    pushSupported: snapshot.pushSupported,
    pushLoading: snapshot.pushLoading,

    // Actions
    markAsRead: controller.markAsRead.bind(controller),
    markReadByDedupKeys: controller.markReadByDedupKeys.bind(controller),
    markReadByIds: controller.markReadByIds.bind(controller),
    deleteNotification: controller.deleteNotification.bind(controller),
    refreshNotifications: controller.refreshNotifications.bind(controller),
    refreshMarkers: controller.refreshMarkers.bind(controller),
    markAllAsRead: controller.markAllAsRead.bind(controller),
    deleteAllNotifications: controller.deleteAllNotifications.bind(controller),

    // Push actions
    enablePush: controller.enablePush.bind(controller),
    disablePush: controller.disablePush.bind(controller),
    getPushStatus: controller.getPushStatus.bind(controller),
    sendTestPush: controller.sendTestPush.bind(controller),
    checkPushPermission: controller.checkPushPermission.bind(controller),
  };
}
